import { UserFavoritesResponse, ErrorResponse, SharedTicketResponse, PrematchLeaguesResponse } from "./interfaces/v1";
import { FavouriteSportId } from "@/shared/model/favourites";
import axios from "axios";
import { apiv1HttpClient } from "./v1-api-http-client";
import { LiveSportsCount } from "./interfaces/live-sports-count";
import { Menu } from "./interfaces/menu";
import { EventDrilldown } from "@/shared/model/event";
import qs from "qs";
import { logCustomException, logException } from "@/shared/utils/logger/use-app-logger";
import { SharedTicket } from "./interfaces/shared-ticket";
import { PageSeo } from "./interfaces/page-seo";
import useUtils from "@/shared/composables/use-utils";
import { Snackbar } from "@/shared/model/snackbar";
import { BetData } from "@/shared/model/bet";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";
import useSazkaSnackbar from "@/shared/composables/use-sazka-snackbar";
import analytics from "@/shared/analytics";

const getUserFavourites = async (): Promise<UserFavoritesResponse | ErrorResponse> => {
    try {
        const response = await apiv1HttpClient.get<UserFavoritesResponse>("/userfavorites");
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return error.response.data as ErrorResponse;
        }
        logException("favourites", error, { message: "Error with fetching user favourites" });
        throw new Error("An unexpected error occurred");
    }
};

const setUserFavourites = async (favorites: {
    sports: FavouriteSportId[];
    leagues: string[];
    events: string[];
    markets: string[];
}): Promise<void> => {
    try {
        const response = await apiv1HttpClient.put("/userfavorites", favorites);

        if (response.status === 401) {
            logCustomException("favourites", "User is not logged in", {
                message: "An error has occurred while saving favourites, user is not logged in",
            });
        }
    } catch (error) {
        console.error("An error has occurred while saving favourites:", error);
        logException("favourites", error, { message: "An error has occurred while saving favourites" });
    }
};

const getLiveSportsCount = async (): Promise<LiveSportsCount> => {
    try {
        const response = await apiv1HttpClient.get("/openbet/live");
        return response.data;
    } catch (error) {
        console.error("An error has occurred while fetching live sports count:", error);
        return { sports: [] };
    }
};

const getMenu = async () => {
    try {
        const response = await apiv1HttpClient.get<Menu>("/menu");

        return response.data;
    } catch (error) {
        console.error("An error has occurred while fetching menu:", error);
        return {} as Menu;
    }
};

const getTrendingEventsWithBetratio = async (): Promise<EventDrilldown[]> => {
    try {
        const response = await apiv1HttpClient.get("/openbet/trending-events");
        return response.data;
    } catch (error) {
        logException("trending-events", error, { message: "Error with fetching trending events" });
    }
};

const shareTicket = async (ticket: BetData): Promise<void> => {
    const { copyTextToClipboard } = useUtils();
    const { isMobileOrTablet } = useIsBreakpoint();

    analytics.sendShareTicket(ticket);

    const successOptions: Partial<Snackbar> = {
        message: "Odkaz pro sdílení byl zkopírován",
        icon: "share",
    };

    const errorOptions: Partial<Snackbar> = {
        message: "Nepovedlo se zkopírovat odkaz pro sdílení",
        icon: "close",
        type: "error",
    };

    try {
        const response: SharedTicketResponse = await apiv1HttpClient.post("/sharedticket", ticket);
        const sportBetsRootUrlWithoutTrailingSlash = environment.sportBetsRootUrl.replace(/\/$/, ""); // Remove trailing slash if it exists
        const copyText = `${window.location.origin + sportBetsRootUrlWithoutTrailingSlash + response.data?.sharedTicketUrl}`;

        if (response.status === 200) {
            if (isMobileOrTablet()) {
                return navigator.share({
                    url: copyText,
                    title: document.title,
                    text: "",
                });
            } else {
                copyTextToClipboard(copyText, successOptions, errorOptions);
            }
        } else {
            useSazkaSnackbar().createSnackbar({
                message: "Něco se nepovedlo. Zkuste to prosím znovu.",
                type: "error",
                duration: 4000,
                icon: "close",
            });
            throw new Error(`Unexpected response status: ${response.status}`);
        }
    } catch (error) {
        console.error("An error occurred: ", error);
        useSazkaSnackbar().createSnackbar({
            message: "Něco se nepovedlo. Zkuste to prosím znovu.",
            type: "error",
            duration: 4000,
            icon: "close",
        });
    }
};

const getSharedTicket = async (ticketId: string): Promise<SharedTicket> => {
    try {
        const response = await apiv1HttpClient.get(`/sharedticket/${ticketId}`);

        return response.data;
    } catch (error) {
        console.error("an error occurred: ", error);
    }
};

const getPrematchLeagues = async (leagueIds: number[] | string[]): Promise<PrematchLeaguesResponse> => {
    try {
        const response = await apiv1HttpClient.get("/openbet/prematch-leagues", {
            params: {
                ids: leagueIds,
            },
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: "repeat" });
            },

            validateStatus: (status) => {
                return (status >= 200 && status < 300) || status === 204;
            },
        });

        if (response.status === 204) {
            console.log("No content available for these leagues", leagueIds);
            return {
                data: [],
                status: 204,
                needsFallback: true,
                message: "No content available",
            };
        }

        return {
            data: response.data,
            status: response.status,
            needsFallback: false,
        };
    } catch (error) {
        logException("api", error, {
            message: "Error fetching prematch leagues data",
            leagueIds,
        });

        return {
            data: [],
            status: error.response?.status || 500,
            needsFallback: true,
            error: error,
            message: "API request failed",
        };
    }
};

const getPageContent = async (urlPath: string): Promise<PageSeo> => {
    try {
        const response = await apiv1HttpClient.get("/page", {
            params: {
                url: urlPath,
            },
        });
        return response.data;
    } catch (error) {
        logException("page", error, { message: "Error with fetching trending events" });
    }
};

export {
    getUserFavourites,
    setUserFavourites,
    getLiveSportsCount,
    getMenu,
    getTrendingEventsWithBetratio,
    shareTicket,
    getSharedTicket,
    getPrematchLeagues,
    getPageContent,
};
